import { useEffect, useState } from 'react'
import { useParams } from 'wouter'
import './styles/index.css'

import { useAuth } from './useAuth'
import { authedFetch } from './authedFetch'
import FeedList from './FeedList'
import StoryList from './StoryList'

export default function App() {
  const { user } = useAuth()
  const params = useParams<{ id?: string }>()
  const selectedFeedId: FeedId = params.id ? parseInt(params.id, 10) : 0

  const [feeds, setFeeds] = useState<Record<FeedId, Feed>>({})
  const [stories, setStories] = useState<Record<StoryId, Story>>({})
  const [keep, setKeep] = useState<Set<StoryId>>(new Set())
  const [loading, setLoading] = useState(true)

  // Load feed list
  useEffect(() => {
    const loadFeeds = async () => {
      const res = await authedFetch('/api/my/feeds')
      if (res.ok) {
        const data: FeedListResponse = await res.json()
        setFeeds(data.feeds)
      }
    }
    loadFeeds()
  }, [])

  // Load stories for a feed
  const loadStories = async (feedId: FeedId) => {
    setLoading(true)
    setKeep(new Set())
    document.getElementById('articles')?.scrollTo(0, 0)

    try {
      const res = await authedFetch(`/api/my/stories?feed_id=${feedId}`)
      if (res.ok) {
        const data: StoryListResponse = await res.json()
        setStories(prev => ({ ...prev, ...data.stories }))
      }
    } finally {
      setLoading(false)
    }
  }

  // Initial load: always fetch all stories to populate sidebar
  useEffect(() => {
    loadStories(0)
  }, [])

  // Reset UI state when selected feed changes
  useEffect(() => {
    setKeep(new Set())
    document.getElementById('articles')?.scrollTo(0, 0)
  }, [selectedFeedId])

  // Filter visible stories: unread + recently read (in keep)
  const getVisibleStories = (): Story[] | null => {
    if (loading) {
      return null
    }
    return Object.values(stories)
      .filter(story => {
        // Show stories marked read since last feed change
        if (keep.has(story.id)) {
          return true
        }
        // Hide read stories
        if (story.is_read) {
          return false
        }
        // Show unread stories for selected feed (or all if 0)
        return selectedFeedId === 0 || story.feed_id === selectedFeedId
      })
      .sort((a, b) => b.publish_time - a.publish_time)
  }

  // Mark story as read
  const handleRead = (id: StoryId) => {
    const story = stories[id]
    if (!story || story.is_read) {
      return
    }
    setStories(prev => ({
      ...prev,
      [id]: { ...prev[id], is_read: true },
    }))
    setKeep(prev => new Set(prev).add(id))
    authedFetch('/api/my/stories/update', {
      method: 'POST',
      body: new URLSearchParams({ id: String(id), action: 'read' }),
    })
  }

  // Save story
  const handleSave = (id: StoryId) => {
    setStories(prev => ({
      ...prev,
      [id]: { ...prev[id], is_saved: true },
    }))
    authedFetch('/api/my/stories/update', {
      method: 'POST',
      body: new URLSearchParams({ id: String(id), action: 'save' }),
    })
  }

  // Unsave story
  const handleUnsave = (id: StoryId) => {
    setStories(prev => ({
      ...prev,
      [id]: { ...prev[id], is_saved: false },
    }))
    authedFetch('/api/my/stories/update', {
      method: 'POST',
      body: new URLSearchParams({ id: String(id), action: 'unsave' }),
    })
  }

  return (
    <div id="singlepage">
      <FeedList
        feeds={feeds}
        isAdmin={user?.isAdmin ?? false}
        onRefresh={() => loadStories(selectedFeedId)}
        selectedFeedId={selectedFeedId}
        stories={stories}
      />
      <StoryList
        stories={getVisibleStories()}
        onRead={handleRead}
        onSave={handleSave}
        onUnsave={handleUnsave}
      />
    </div>
  )
}
